// components/register/register.component.class.jsx
import React, { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { auth, db } from "../../firebase/firebase.utils";

import {
  createOrLoadUserProfile,
  getOldestAllowedBirthDate,
  getVerificationActionSettings,
  isAtLeast16,
  makeDefaultDisplayName,
} from "../../firebase/auth-profile.utils";

import avatarParts from "../avatar-creator/avatarParts";
import Peep from "../avatar-creator/Peep";

import Input from "../retro/input/input.component";
import RetroButton from "../retro/button/retro-button.component";
import Loading from "../retro/loading/loading.component";

import uploadFile from "../../utils/uploadFile";
import createTinyAvatarFile from "../../utils/createTinyAvatarFile";

import "./register.styles.scss";

const makeDefaultAvatar = () => {
  const skinColors = ["#FFD11B", "#dedede", "#1A82C4"];

  const hairColors = [
    "000000",
    "2C1B18",
    "4A312C",
    "724133",
    "A55728",
    "B58143",
    "C93305",
    "D6B370",
    "E8E1E1",
    "ECDCBF",
    "F59797",
  ];

  const randomKey = (category) => {
    const keys = Object.keys(avatarParts?.[category] || {});

    return keys.length ? keys[Math.floor(Math.random() * keys.length)] : null;
  };

  const facialHairKeys = Object.keys(avatarParts?.facialHair || {});

  const noneFacialHairKey =
    facialHairKeys.find((key) => key.toLowerCase().includes("none")) ||
    facialHairKeys[0] ||
    "noneFacialHair";

  return {
    head: randomKey("head") || "afro",
    accessories: randomKey("accessories") || "none",
    body: randomKey("body") || "blazerBlackTee",
    facialHair: noneFacialHairKey,
    face: randomKey("face") || "blank",
    skinColor: skinColors[Math.floor(Math.random() * skinColors.length)],
    hairColor: hairColors[Math.floor(Math.random() * hairColors.length)],
  };
};

const friendlyRegisterError = (error) => {
  switch (error?.code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";

    case "auth/email-already-in-use":
    case "auth/email-already-exists":
      return "That email already has an account. Please log in instead.";

    case "auth/weak-password":
      return "Your password must contain at least 6 characters.";

    case "auth/network-request-failed":
      return "Check your connection and try again.";

    default:
      return "Registration failed. Please try again.";
  }
};

const saveInitialTinyAvatar = (uid, avatarFilePromise) => {
  // Intentionally not part of the critical registration path.
  void avatarFilePromise
    .then(async (file) => {
      if (!file) return;

      const uploaded = await uploadFile({
        file,
        fullPath: `users/${uid}/profile/avatarTiny-${uid}.png`,
        userId: uid,
        parent: `users/${uid}`,
      });

      const avatarTinyUrl = uploaded?.url;

      if (!avatarTinyUrl) {
        throw new Error("Initial avatar upload returned no URL");
      }

      await db.collection("users").doc(uid).set(
        {
          avatarTinyUrl,
        },
        {
          merge: true,
        },
      );
    })
    .catch((error) => {
      // Never fail registration just because
      // the derived avatar image failed.
      console.error("Initial tiny avatar creation failed:", error);
    });
};

const Register = () => {
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [repeatPassword, setRepeatPassword] = useState("");

  const [userName, setUserName] = useState("");

  const [dateOfBirth, setDateOfBirth] = useState(null);

  const [errorMessage, setErrorMessage] = useState("");

  const [showLoading, setShowLoading] = useState(false);

  const [displayNameTouched, setDisplayNameTouched] = useState(false);

  const [displayNameAutoGenerated, setDisplayNameAutoGenerated] =
    useState(false);

  /*
   * Create the signup avatar only once.
   *
   * The exact same avatar is:
   * 1. saved as imgAvatar
   * 2. rendered by the hidden Peep
   * 3. exported as avatarTinyUrl
   */
  const defaultAvatarRef = useRef(makeDefaultAvatar());

  const tinyAvatarRef = useRef(null);

  const handleEmailChange = (value) => {
    setEmail(value);
    setErrorMessage("");

    if (!displayNameTouched && value.includes("@")) {
      setUserName(makeDefaultDisplayName(value));

      setDisplayNameAutoGenerated(true);
    }
  };

  const handleDisplayNameChange = (value) => {
    setDisplayNameTouched(true);
    setUserName(value);
  };

  const handleRefreshDisplayName = () => {
    setUserName(makeDefaultDisplayName(email));

    setDisplayNameAutoGenerated(true);

    setDisplayNameTouched(false);
  };

  const register = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail || !password) {
      setErrorMessage("Email and password are required.");
      return;
    }

    if (password !== repeatPassword) {
      setErrorMessage("Passwords don’t match.");
      return;
    }

    if (password.length < 6) {
      setErrorMessage("Your password must contain at least 6 characters.");
      return;
    }

    if (!dateOfBirth) {
      setErrorMessage("Please select your date of birth.");
      return;
    }

    if (!isAtLeast16(dateOfBirth)) {
      setErrorMessage("You must be 16 or older to create an account.");
      return;
    }

    /*
     * Start the local PNG conversion immediately.
     *
     * This is only local canvas work and can happen
     * alongside the Firebase registration request.
     *
     * We capture the SVG before authentication can
     * cause this screen to unmount.
     */
    const avatarSvg = tinyAvatarRef.current?.querySelector("svg");

    const avatarFilePromise = avatarSvg
      ? createTinyAvatarFile({
          svg: avatarSvg,
          fileName: "avatarTiny.png",
          outputSize: 256,
          flip: true,
        }).catch((error) => {
          console.error("Initial avatar PNG creation failed:", error);

          return null;
        })
      : Promise.resolve(null);

    try {
      setShowLoading(true);

      const credential = await auth.createUserWithEmailAndPassword(
        cleanEmail,
        password,
      );

      const firebaseUser = credential.user;

      const finalName =
        userName.trim() || makeDefaultDisplayName(firebaseUser.email);

      await createOrLoadUserProfile(
        firebaseUser,
        finalName,
        defaultAvatarRef.current,
        dateOfBirth,
      );

      /*
       * Do not await this.
       *
       * The user's account/profile already exists.
       * Storage upload happens independently and
       * cannot delay or fail registration.
       */
      saveInitialTinyAvatar(firebaseUser.uid, avatarFilePromise);

      try {
        await firebaseUser.sendEmailVerification(
          getVerificationActionSettings(),
        );

        sessionStorage.setItem("verificationEmailSentAt", String(Date.now()));
      } catch (verificationError) {
        // The verification page remains available and
        // offers a manual resend.
        console.error("Initial verification email failed:", verificationError);
      }

      // No Redux dispatch and no reload are needed.
      // App.js owns auth routing and will move this
      // signed-in, unverified user to /verify-email.
    } catch (error) {
      console.error("Registration failed:", error);

      setErrorMessage(friendlyRegisterError(error));
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <div className="register">
      {showLoading && <Loading />}

      {/*
       * Off-screen renderer used only to generate
       * the initial avatarTiny PNG.
       *
       * Don't use display:none or visibility:hidden,
       * because getBBox() needs the SVG to be laid out.
       */}
      <div
        ref={tinyAvatarRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -10000,
          top: -10000,
          width: 256,
          height: 256,
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        <Peep
          useravatar={defaultAvatarRef.current}
          size={256}
          width={256}
          height={256}
          borderRadius={128}
          borderWidth={0}
          overflow="visible"
          backgroundColor="transparent"
          tiny
          flip
        />
      </div>

      <div className="register__image">
        <img src="/homepage/signup.png" width="360" alt="Create an account" />
      </div>

      <div className="register__formWrap">
        <h2>Register</h2>

        <form onSubmit={register}>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            autoComplete="email"
            onChange={(event) => handleEmailChange(event.target.value)}
          />

          <div className="register__displayNameRow">
            <div className="register__grow">
              <Input
                type="text"
                placeholder="Display name"
                value={userName}
                autoComplete="nickname"
                onChange={(event) =>
                  handleDisplayNameChange(event.target.value)
                }
              />
            </div>

            <button
              className="register__refreshName"
              type="button"
              onClick={handleRefreshDisplayName}
              title="Regenerate display name"
              aria-label="Regenerate display name"
            >
              ↻
            </button>
          </div>

          <Input
            type="password"
            placeholder="Password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
          />

          <Input
            type="password"
            placeholder="Repeat password"
            value={repeatPassword}
            autoComplete="new-password"
            onChange={(event) => setRepeatPassword(event.target.value)}
          />

          <div className="register__birthday">
            <label htmlFor="registration-date-of-birth">Date of birth</label>

            <DatePicker
              id="registration-date-of-birth"
              selected={dateOfBirth}
              onChange={setDateOfBirth}
              maxDate={getOldestAllowedBirthDate()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              dateFormat="MMM d, yyyy"
              placeholderText="Select date"
              autoComplete="bday"
            />

            <small>
              Used to confirm you’re 16 or older and support safety features.
              Your exact birthday is stored privately and is not shown on your
              profile.
            </small>
          </div>

          <RetroButton
            type="submit"
            onClick={register}
            style={{
              width: "100%",
            }}
          >
            Register
          </RetroButton>
        </form>

        {errorMessage && (
          <div className="register__error" role="alert">
            <p>{errorMessage}</p>
          </div>
        )}

        {displayNameAutoGenerated && (
          <p className="register__nameHint">
            Your display name is autogenerated. You can change it anytime.
          </p>
        )}
      </div>
    </div>
  );
};

export default Register;
